import React, { useState } from 'react';
import { Box, Typography, Grid, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { youtubeAPI } from '../../services/api';
import SectionTitle from '../crt/SectionTitle';
import Panel from '../crt/Panel';

const RetentionInsights: React.FC = () => {
  const { data: overview } = useQuery({
    queryKey: ['retention-overview', 365],
    queryFn: () => youtubeAPI.getRetentionOverview(365),
    staleTime: 60000,
  });

  const { data: videosData } = useQuery({
    queryKey: ['retention-videos'],
    queryFn: () => youtubeAPI.getRetentionVideos(500),
    staleTime: 120000,
  });

  const [currentVid, setCurrentVid] = useState('');
  const { data: curveData } = useQuery({
    queryKey: ['retention-curve', currentVid],
    queryFn: () => youtubeAPI.getRetentionCurve(currentVid, 'auto'),
    enabled: !!currentVid,
    staleTime: 60000,
  });

  const checkpoints = (overview?.checkpoints || []).map((c: any) => ({
    name: `${c.pct}%`,
    retention: Number(c.retention),
  }));

  const curvePoints = (curveData?.points || []).map((p: any) => ({
    pct: p.x,
    retention: Number(p.retention_pct),
  }));

  return (
    <Box>
      <SectionTitle>RETENTION INSIGHTS</SectionTitle>
      <Grid container spacing={3}>
        {/* Overview bar chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="AVERAGE RETENTION CHECKPOINTS">
            {checkpoints.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={checkpoints} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
                  <Bar dataKey="retention" fill="#00ff41" />
                </BarChart>
              </ResponsiveContainer>
            ) : overview?.available === false ? (
              <Typography color="textSecondary">{overview?.note || 'Retention data not available yet'}</Typography>
            ) : (
              <Typography color="textSecondary">Loading...</Typography>
            )}
          </Panel>
        </Grid>

        {/* Retention curve */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="VIDEO RETENTION CURVE">
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Select Video</InputLabel>
              <Select
                value={currentVid}
                label="Select Video"
                onChange={(e) => setCurrentVid(e.target.value as string)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
              >
                {(videosData?.videos || []).map((v: any) => (
                  <MenuItem key={v.video_id} value={v.video_id}>
                    {v.title?.substring(0, 55)}{v.title?.length > 55 ? '...' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {curvePoints.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={curvePoints} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="pct"
                    tickFormatter={(v) => `${v}%`}
                    interval={19}
                  />
                  <YAxis domain={[0, 'auto']} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    formatter={(v: number) => `${v.toFixed(1)}%`}
                    labelFormatter={(l) => `${l}% through video`}
                  />
                  <Area
                    type="monotone"
                    dataKey="retention"
                    stroke="#00ff41"
                    fill="#00ff41"
                    fillOpacity={0.15}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : currentVid ? (
              <Typography color="textSecondary">No retention data for this video.</Typography>
            ) : (
              <Typography color="textSecondary">Select a video to see its retention curve.</Typography>
            )}
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RetentionInsights;
